/** @format */

import {
  download as downloadSHP,
  type WriterOptions,
} from "@crmackey/shp-write";
import { elementToSVG, inlineResources } from "dom-to-svg";
import type { FeatureCollection } from "geojson";
import JSZip from "jszip";
import type { ChartViews } from "senf-atomic-design-system/src/components/special/survey/hooks";
import type { Survey } from "senf-atomic-design-system/src/components/special/survey/survey.config";
import NunitoBold from "senf-tokens/fonts/nunito-v25-latin-700.woff2";
import NunitoRegular from "senf-tokens/fonts/nunito-v25-latin-regular.woff2";
import { utils, type WorkBook, type WorkSheet, writeFile } from "xlsx-js-style";
import type { ExportItemProps } from "../../../apps/senf-monitoring/src/components/export/getExportItem";
import { ConcurrencyQueue } from "./ConcurrencyQueue";
import { createSafeFilename } from "./helpers";
export interface Sheet {
  data: Record<string, any>[];
  header: string[];
  name?: string;
  size?: string;
  convertData?: boolean;
  mergeQuestionCells?: boolean;
  headerRows?: number;
}
export interface ExportFileProps {
  fileName: string;
  survey?: Survey;
}
export interface ExportXlsxProps extends ExportFileProps {
  sheets: Sheet[];
  download?: boolean;
  emptyTrailingColumns?: number;
}
export interface ExportJSONProps extends ExportFileProps {
  data: any;
  flatten?: boolean;
  extension?: "json";
}
export interface ExportGeoJSONProps extends ExportFileProps {
  data: FeatureCollection;
  flatten?: boolean;
  extension?: "geojson";
}
export interface ExportShapeFileProps
  extends Omit<ExportGeoJSONProps, "extension"> {
  options?: WriterOptions;
}

export const download = (url: string, fileName: string) => {
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  link.click();
  link.remove();
};

// Utility functions and constants

const MAX_CHAR_LENGTH = 32767;

const metaBlockColors = {
  light: "f5f3f1",
  dark: "eceae7",
};
const questionBlockColors = {
  light: [
    "eff2fe",
    "fff1cf",
    "fdefed",
    "e7f5e8",
    "fcedf4",
    "ffeede",
    "f4eeff",
    "e0f5f8",
    "f8f1eb",
  ],
  dark: [
    "dfe6fd",
    "ffe4a6",
    "fce2dd",
    "d5eed6",
    "f9e1ec",
    "ffe3c6",
    "eee3ff",
    "c9eef2",
    "f2e5d9",
  ],
};

const truncateText = (text: string) => {
  if (text.length > MAX_CHAR_LENGTH) {
    console.warn(`Text truncated to ${MAX_CHAR_LENGTH} characters:`, text);
    return text.slice(0, MAX_CHAR_LENGTH);
  }
  return text;
};

const convertData = (data: Sheet["data"]) => {
  return data?.map((res) =>
    Object.values(res).map((value) => {
      if (value == null) {
        return " ";
      }
      if (Array.isArray(value)) {
        return truncateText(value.sort().join(", "));
      }
      if (typeof value === "object") {
        return truncateText(JSON.stringify(value));
      }
      if (typeof value === "string") {
        return truncateText(value);
      }
      return value as string;
    }),
  );
};
const padData = (data: string[][], emptyTrailingColumns = 0) => {
  const maxLength = data.reduce((max, arr) => Math.max(max, arr.length), 0);
  return data.map(
    (arr) =>
      [
        ...arr,
        ...Array(maxLength + emptyTrailingColumns - arr.length).fill(" "),
      ] as string[],
  );
};
const getQuestionColumns = (worksheet: WorkSheet): Record<string, number> => {
  const result: Record<string, number> = {};
  const lightColorsLength = questionBlockColors.light.length;
  const rowRegex = /\d+$/;
  const colRegex = /^\D+/;

  for (const key in worksheet) {
    const value = worksheet[key];
    const rowMatch = key.match(rowRegex);
    if (!rowMatch) continue;

    const row = +rowMatch[0];
    if (row > 2) continue;

    const val = value?.v;
    if (typeof val !== "string" || !val.startsWith("Q")) continue;

    const qIndex = +val.slice(1).split("_")[0] - 1;
    const colMatch = key.match(colRegex);
    if (!colMatch) continue;

    result[colMatch[0]] = qIndex % lightColorsLength;
  }

  return result;
};

const setColumnWidths = ({
  worksheet,
  cols,
}: {
  worksheet: WorkSheet;
  cols: number;
}): WorkSheet => {
  worksheet["!cols"] = Array(cols).fill({ wch: 15 });
  return worksheet;
};

const applyStylesAndMerge = ({
  worksheet,
  questionCols,
  cols,
  rows,
  merge,
  headerRows = 1,
  emptyTrailingColumns = 0,
  clearNonQuestionHeaderCells = false, // @todo: do not use in Survey 2.0
}: {
  worksheet: WorkSheet;
  questionCols: Record<string, number>;
  cols: number;
  rows: number;
  merge: boolean | undefined;
  headerRows?: number;
  emptyTrailingColumns?: number;
  clearNonQuestionHeaderCells?: boolean;
}): WorkSheet => {
  const ws = worksheet;
  const border = { style: "thin", color: { rgb: "FFFFFF" } };

  let prevQuestion = 0;
  let prevColorIndex = -1;
  let mergeStart = "";
  const merges = [];

  for (let c = 0; c < cols; c++) {
    const col = utils.encode_col(c);
    const colorIdx = questionCols[col] ?? -1;
    const colorLight =
      colorIdx > -1
        ? questionBlockColors.light[colorIdx]
        : metaBlockColors.light;
    const colorDark =
      colorIdx > -1 ? questionBlockColors.dark[colorIdx] : metaBlockColors.dark;

    const colIsTrailing = cols - (c + 1) < emptyTrailingColumns;

    for (let r = 1; r <= rows + 1; r++) {
      const addr = `${col}${r}`;
      const cell = ws[addr];
      if (!cell) continue;

      // Wipe trailing columns
      if (colIsTrailing) {
        cell.v = " ";
        continue;
      }

      const isHeaderRow = r <= headerRows;

      // --- Merge logic ---
      if (r === 1 && merge) {
        const qVal = ws[`${col}2`]?.v;
        const qStr = typeof qVal === "string" ? qVal : "";
        const startsWithQ = qStr.startsWith("Q");
        const questionNum = startsWithQ
          ? Number.parseInt(qStr.split("_", 1)[0].slice(1), 10)
          : 0;

        if (startsWithQ) {
          if (!mergeStart) mergeStart = col;

          // Set value for merge header
          const newVal =
            prevQuestion !== questionNum
              ? typeof cell.v === "string"
                ? cell.v.replace(/[\r\n]+/g, " ")
                : cell.v
              : " ";
          cell.v = newVal;

          const isLastCol = c === cols - 1 - emptyTrailingColumns;
          const colorChanged = colorIdx !== prevColorIndex;

          if (isLastCol || colorChanged) {
            const s = { r: 0, c: utils.decode_col(mergeStart) };
            const e = { r: 0, c: isLastCol && !colorChanged ? c : c - 1 };
            if (s.c < e.c) merges.push({ s, e });
            mergeStart = col;
          }

          prevColorIndex = colorIdx;
        } else if (clearNonQuestionHeaderCells) {
          cell.v = " ";
        }

        prevQuestion = questionNum;
      }

      // --- Style and type logic ---
      const val = cell.v;
      const isStr = typeof val === "string";
      const isNum = typeof val === "number";
      const isInt = isNum && Number.isInteger(val);

      const fillColor = !isHeaderRow && r % 2 ? colorLight : colorDark;

      ws[addr] = {
        ...cell,
        s: {
          font: { bold: isHeaderRow },
          fill: { fgColor: { rgb: fillColor } },
          alignment: { vertical: "top" },
          border: {
            top: r === headerRows + 1 ? { ...border, style: "thick" } : border,
            right: border,
            bottom: border,
            left: border,
          },
        },
        t: isInt ? "n" : cell.t,
        z: isInt ? "0" : cell.z,
        v: isInt ? val : isStr ? val.trimEnd() : val,
      };
    }
  }

  if (merge && merges.length > 0) ws["!merges"] = merges;

  return ws;
};

// @todo: extract the styling functions to a separate file, and pass it to the export function, so that different styles can be used for different exports
export const exportXlsx = ({
  fileName,
  sheets,
  download = true,
  emptyTrailingColumns = 1,
}: ExportXlsxProps): Promise<WorkBook> => {
  return new Promise((resolve, reject) => {
    try {
      const workbook = utils.book_new();
      for (const sheet of sheets.filter((e) => !!e?.data?.length)) {
        const data = convertData(sheet.data);

        const paddedData = padData(data, emptyTrailingColumns);

        const cols = sheet.header.length + emptyTrailingColumns;
        const rows = paddedData.length;

        let worksheet = utils.json_to_sheet(paddedData);

        worksheet = utils.sheet_add_aoa(worksheet, [sheet.header], {
          origin: "A1",
        });

        const questionCols = getQuestionColumns(worksheet);

        worksheet = setColumnWidths({ worksheet, cols });

        worksheet = applyStylesAndMerge({
          worksheet,
          questionCols,
          cols,
          rows,
          merge: sheet.mergeQuestionCells,
          headerRows: sheet.headerRows,
          emptyTrailingColumns,
          clearNonQuestionHeaderCells: true,
        });

        // Append the completed worksheet to the workbook

        utils.book_append_sheet(workbook, worksheet, sheet.name || "Results");
      }

      if (download) {
        // Write the workbook to a file

        writeFile(workbook, `${fileName}.xlsx`, { compression: true });
      }
      resolve(workbook);
    } catch (err) {
      console.error(err);
      reject(
        "Unfortunately an error occured when downloading the file. Please try again or contact us.",
      );
    }
  });
};

export const exportJSON = ({
  fileName,
  data,
  extension = "json",
}: ExportJSONProps | ExportGeoJSONProps): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const url = `data:text/json;charset=utf-8,${encodeURIComponent(
        JSON.stringify(data),
      )}`;
      download(url, `${fileName}.${extension}`);
      resolve();
    } catch (err) {
      console.error(err);
      reject(
        "Unfortunately an error occured when downloading the file. Please try again or contact us.",
      );
    }
  });
};

export const exportShapeFile = async ({
  fileName,
  data,
  options,
}: ExportShapeFileProps): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      const defaultOptions: WriterOptions = {
        name: fileName,
        ...options,
      };

      if (!data.features) throw "FeatureCollection not available";
      const types = data.features.reduce(
        (
          acc: Record<string, FeatureCollection>,
          e: FeatureCollection["features"][number],
        ) => {
          if (!acc[e.geometry.type])
            acc[e.geometry.type] = {
              type: "FeatureCollection",
              features: [],
            };
          acc[e.geometry.type].features.push(e);
          return acc;
        },
        {},
      );

      // We need to download each type separately, because the shp-write library doesn't support multiple types in one file
      Object.keys(types).forEach((e: keyof typeof types, _, arr) => {
        const name =
          arr.length > 1 ? `${defaultOptions.name}_${e}` : defaultOptions.name;

        downloadSHP(types[e], {
          ...defaultOptions,
          name: `${name}`,
        });
      });
      resolve();
    } catch (err) {
      console.error(err);
      reject(
        "Unfortunately an error occured when downloading the file. Please try again or contact us.",
      );
    }
  });
};

export const exportSheets = (props: ExportXlsxProps): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      exportXlsx(props);
      resolve();
    } catch (err) {
      console.error(err);
      reject(
        "Unfortunately an error occured when downloading the file. Please try again or contact us.",
      );
    }
  });
};

function exportMapToImage(map: mapboxgl.Map) {
  const canvas = document.createElement("canvas");
  canvas.width = map.getCanvas().width;
  canvas.height = map.getCanvas().height;
  const ctx = canvas.getContext("2d");
  ctx?.drawImage(map.getCanvas(), 0, 0);
  return canvas.toDataURL("image/png");
}

export const exportMap = (
  map?: mapboxgl.Map,
  fileName = "map-img",
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (!map) throw "The map cannot be downloaded";
      const dataUrl = exportMapToImage(map);
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${fileName}.png`;
      link.click();
      resolve();
    } catch (err) {
      console.error(err);
      reject(err);
    }
  });
};

function compileSvgs(clone: HTMLElement) {
  for (const e of clone.querySelectorAll("use")) {
    const href = e.getAttribute("href") || "";
    const targetElement = document.querySelector(href);

    if (targetElement) {
      const cloneChildren = Array.from(targetElement.children).map((child) =>
        child.cloneNode(true),
      );
      for (const f of cloneChildren) {
        e.parentElement?.appendChild(f);
      }
    }
  }
}
// Function to check if an element has computed overflow style set to "auto"
const hasAutoOverflow = (element: Element) =>
  window.getComputedStyle(element).overflow === "auto";

// Function to find all children of an element with computed overflow style of "auto"
const findChildrenWithAutoOverflow = (parentElement: Element): Element => {
  const [parent] = Array.from(parentElement.children)
    .reduce(
      (result, child) =>
        result.concat(
          hasAutoOverflow(child) ? [child] : [],
          findChildrenWithAutoOverflow(child),
        ),
      [] as Element[],
    )
    .filter(Boolean);

  return parent;
};

const svgCache = new Map<string, DocumentFragment>();

const resolveSvg = async (useElement: SVGUseElement) => {
  const href = useElement.getAttribute("href") || "";
  if (!href) return;

  if (!svgCache.has(href)) {
    const targetElement = document.querySelector(href);
    if (targetElement) {
      const fragment = document.createDocumentFragment();
      for (const child of targetElement.children) {
        fragment.appendChild(child.cloneNode(true));
      }
      svgCache.set(href, fragment);
    }
  }

  const fragment = svgCache.get(href);
  if (fragment) {
    useElement.parentElement?.appendChild(fragment.cloneNode(true));
  }
};

interface ChartStyle {
  width?: number;
  height?: number;
  scale?: number;
  [key: string]: any;
}

export const exportChartAsPNG = ({
  ref,
  fileName,
  downloadImmediately,
}: DownloadChartProps): Promise<Blob | undefined> => {
  if (!ref.current) return Promise.resolve(undefined);

  return new Promise<Blob | undefined>((resolve) => {
    exportChartAsSVG({
      ref,
      fileName,
    }).then((svg) => {
      if (!svg) return;
      convertSVGToPNG(svg).then((png) => {
        if (downloadImmediately) {
          const url = URL.createObjectURL(png);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${fileName}.png`;
          link.click();
          URL.revokeObjectURL(url);
          resolve(undefined);
        } else {
          resolve(png);
        }
      });
    });
  });

  // return new Promise((resolve, reject) => {

  //   const elem = document.getElementById("bubbleChartCheckbox");
  //   if (elem) {
  //     elem.style.visibility = "hidden";
  //   }

  //   const exportChart = async () => {
  //     // Expensive: Deep clone of DOM node
  //     const clone = chartRef.current.cloneNode(true) as HTMLElement;
  //     clone.classList.add("html2canvas", "exported-chart");

  //     Object.assign(clone.style, {
  //       position: "absolute",
  //       left: "-9999px",
  //       top: "-9999px",
  //       width: style.width ? `${style.width}px` : undefined,
  //       height: style.height ? `${style.height}px` : undefined,
  //       ...style,
  //     });

  //     document.body.appendChild(clone);

  //     try {
  //       // Most expensive: Canvas rendering of complex DOM structure
  //       const canvas = await html2canvas(clone, {
  //         width: style.width || clone.offsetWidth,
  //         height: style.height || clone.offsetHeight,
  //         useCORS: true,
  //         scrollX: 0,
  //         scrollY: 0,
  //         backgroundColor: null,
  //         scale: style.scale || 2,
  //         logging: false,
  //         ignoreElements: (el) => (
  //           el.classList.contains("hide-in-exported-chart") ||
  //           el.classList.contains("table-sort-icon") ||
  //           el.classList.contains("scroll-bar")
  //         )
  //       });
  //       // Expensive: Canvas blob conversion
  //       const blob = await new Promise<Blob>((resolve) => {
  //         canvas.toBlob((blob) => resolve(blob!), 'image/png', 1);
  //       });

  //       if (downloadImmediately) {
  //         const url = URL.createObjectURL(blob);
  //         const link = document.createElement("a");
  //         link.href = url;
  //         link.download = `${fileName}.png`;
  //         link.click();
  //         URL.revokeObjectURL(url);
  //         resolve();
  //       } else {
  //         resolve(blob);
  //       }
  //     } catch (error) {
  //       reject(error);
  //     } finally {
  //       document.body.removeChild(clone);
  //       if (elem) {
  //         elem.style.visibility = "visible";
  //       }
  //     }
  //   };

  //   if ('requestIdleCallback' in window) {
  //     requestIdleCallback(exportChart);
  //   } else {
  //     setTimeout(exportChart, 1);
  //   }
  // });
};

export const fetchFontAsBase64 = async <T extends Record<string, string>>(
  fonts: Record<string, T>,
): Promise<Record<string, T>> => {
  try {
    const fontEntries = await Promise.all(
      Object.entries(fonts).map(async ([fontName, weights]) => {
        const weightEntries = await Promise.all(
          Object.entries(weights).map(async ([weight, fontUrl]) => {
            const response = await fetch(fontUrl);
            if (!response.ok) {
              throw new Error(
                `Failed to fetch font ${fontName} with weight ${weight}`,
              );
            }
            const blob = await response.blob();
            return [weight, await blobToBase64(blob)];
          }),
        );
        return [fontName, Object.fromEntries(weightEntries)];
      }),
    );

    return Object.fromEntries(fontEntries) as Record<string, T>;
  } catch (error) {
    console.error("Error fetching fonts:", error);
    throw error;
  }
};

// Helper function to convert Blob to Base64
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject("Failed to convert Blob to Base64");
    reader.readAsDataURL(blob);
  });
};

// Function to inline the base64 font into the SVG
const inlineFonts = (
  svgElement: Element,
  fonts: Record<string, Record<string, string>>,
) => {
  // Create a style tag with embedded font
  const styleElement = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "style",
  );
  styleElement.textContent = "";
  for (const [name, weights] of Object.entries(fonts)) {
    for (const [weight, base64Font] of Object.entries(weights)) {
      styleElement.textContent += `
      @font-face {
        font-family: '${name}';
        src: url(${base64Font}) format('woff2');
        font-weight: ${weight};
      }
      text {
        font-family: '${name}', sans-serif;
      }
    `;
    }
  }

  // Append the style to the SVG's <defs> or <style> section
  const defs = svgElement.querySelector("defs");
  if (defs) {
    defs.appendChild(styleElement);
  } else {
    svgElement.appendChild(styleElement);
  }
};

const mapColors = (svgElement: Element) => {
  const colorMap: Record<string, string> = {};

  // Get all text elements
  const textElements = svgElement.querySelectorAll("text");

  for (const text of textElements) {
    const fill = text.getAttribute("fill");
    if (!text.textContent) return;
    const rgb = fill?.startsWith("rgb") ? fill.match(/\d+/g) : null;
    if (rgb) {
      const hex = `#${rgb.map((c) => (+c).toString(16).padStart(2, "0")).join("")}`;
      colorMap[text.textContent] = hex;
    } else if (fill) {
      colorMap[text.textContent] = fill;
    }
  }

  return colorMap;
};

const reassignTextColors = (
  element: HTMLElement,
  map: Record<string, string>,
) => {
  const textNodes = element.querySelectorAll("text");
  for (const node of textNodes) {
    const textContent = node.textContent || "";
    if (map[textContent]) {
      node.setAttribute("fill", map[textContent]);
    }
  }
};

const removeElementsByClassName = (element: Element, classNames: string[]) => {
  for (const className of classNames) {
    const elements = element.getElementsByClassName(className);
    for (const e of elements) {
      e.remove();
    }
  }
};

const fonts = {};

type DownloadChartProps = {
  ref: React.MutableRefObject<HTMLElement>;
  fileName: string;
  downloadImmediately?: boolean;
};
export const exportChartAsSVG = async ({
  ref,
  fileName,
  downloadImmediately,
}: DownloadChartProps): Promise<Blob | undefined> => {
  if (!ref.current) return Promise.resolve(undefined);

  ref.current.classList.add("export-as-svg", "exported-chart");

  if (Object.keys(fonts).length === 0) {
    Object.assign(
      fonts,
      await fetchFontAsBase64({
        Nunito: {
          normal: NunitoRegular,
          bold: NunitoBold,
        },
      }),
    );
  }
  try {
    // Inline SVGs referenced through <use> elements
    const useElements = ref.current.querySelectorAll("use");
    for (const useElement of useElements) {
      const href =
        useElement.getAttribute("href") ||
        useElement.getAttribute("xlink:href");
      if (href) {
        const target = document.querySelector(href) as SVGSymbolElement;
        if (target) {
          const svgElement = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          svgElement.innerHTML = target.innerHTML;
          svgElement.setAttribute(
            "viewBox",
            target.getAttribute("viewBox") || "",
          );
          useElement.parentNode?.replaceChild(svgElement, useElement);
        }
      }
    }

    const colorMap = mapColors(ref.current);
    const svgDocument = elementToSVG(ref.current);

    ref.current.classList.remove("export-as-svg", "exported-chart");

    removeElementsByClassName(svgDocument.documentElement, [
      "hide-in-exported-chart",
      "table-sort-icon",
      "scroll-bar",
    ]);
    inlineFonts(svgDocument.documentElement, fonts);
    await inlineResources(svgDocument.documentElement);
    if (colorMap) reassignTextColors(svgDocument.documentElement, colorMap);
    const svgString = new XMLSerializer().serializeToString(svgDocument);
    const blob = new Blob([svgString], { type: "image/svg+xml" });

    if (downloadImmediately) {
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = `${fileName}.svg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      return;
    }

    return blob;
  } finally {
    ref.current.classList.remove("export-as-svg", "exported-chart");
  }
};

export const convertSVGToPNG = (svg: Blob, scale = 4): Promise<Blob> => {
  const svgUrl = URL.createObjectURL(svg);
  const img = new Image();
  img.src = svgUrl;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  return new Promise<Blob>((resolve) => {
    img.onload = async () => {
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx?.scale(scale, scale);
      ctx?.drawImage(img, 0, 0);

      const pngBlob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => resolve(blob as Blob), "image/png", 1);
      });

      resolve(pngBlob);
      URL.revokeObjectURL(svgUrl);
    };
  });
};

export type ExportSurveyChartsProps = Partial<ExportItemProps["props"]> & {
  survey: Survey;
  pageIndex?: number;
  rowIndex?: number;
  tagIndex?: number;
  view?: ChartViews;
  format?: ("png" | "svg")[];
  delay?: number;
};
export const exportSurveyCharts = async ({
  survey,
  chartRefContainer,
  pageIndex,
  rowIndex,
  tagIndex,
  view,
  format = ["png", "svg"],
  setExportProgress,
  abortController = new AbortController(),
}: ExportSurveyChartsProps): Promise<void> => {
  if (!chartRefContainer?.current) {
    throw "chartRefContainer is not defined";
  }
  const { signal } = abortController;

  try {
    setExportProgress?.((e) => ({ ...e, isExporting: true }));

    const estimatedDelay =
      10 *
      (pageIndex ? 1 : survey.pages.length) *
      (pageIndex && rowIndex != null ? 1 : 5) *
      (view ? 1 : 5);

    // Simulate a delay (replace this with real logic)
    await new Promise((resolve) =>
      requestAnimationFrame(() => setTimeout(resolve, estimatedDelay)),
    );

    const questionSelector = pageIndex
      ? `[data-chart-question="${survey.pages?.[pageIndex].uid}"]`
      : "[data-chart-question]";
    const rowSelector =
      rowIndex != null ? `[data-chart-row="${rowIndex}"]` : "";
    const tagIndexSelector =
      tagIndex != null ? `[data-chart-tag="${tagIndex}"]` : "";
    const viewSelector = view ? `[data-chart-view="${view}"]` : "";
    // Collect charts to export
    const allCharts = Array.from(
      chartRefContainer.current?.querySelectorAll(
        [questionSelector, rowSelector, tagIndexSelector, viewSelector].join(
          "",
        ),
      ),
    ) as HTMLElement[];

    if (allCharts.length === 0) {
      throw "No charts found to export";
    }

    setExportProgress?.((e) => ({ ...e, current: 1, total: allCharts.length }));

    // Create a concurrency queue with a cancelable task
    const queue = new ConcurrencyQueue(3);
    const chartBlobs: { blob: Blob; name: string; extension: string }[] = [];

    const promises = allCharts.map((chart, index) => {
      return queue.add(async () => {
        if (signal.aborted) throw new Error("Export process canceled");

        const fileName =
          chart.getAttribute("data-chart-name") || `chart_${index + 1}`;
        try {
          const svg = await exportChartAsSVG({
            ref: { current: chart as HTMLElement },
            fileName,
          });

          if (svg instanceof Blob) {
            if (format.includes("png")) {
              const png = await convertSVGToPNG(svg);
              chartBlobs.push({
                blob: png,
                name: `png/${fileName}`,
                extension: "png",
              });
            }
            if (format.includes("svg")) {
              chartBlobs.push({
                blob: svg,
                name: `svg/${fileName}`,
                extension: "svg",
              });
            }
          }

          setExportProgress?.((e) => ({
            ...e,
            current: e.current + 1,
          }));
        } catch (error) {
          console.error(error);
          throw `Failed to export chart ${fileName}:`;
        }
      });
    });

    // Wait for all tasks to finish
    await Promise.all(promises);

    // Create and download ZIP
    if (chartBlobs.length > 1) {
      if (signal.aborted) throw new Error("Export process canceled");

      setExportProgress?.((prev) => ({ ...prev, status: "compressing" }));
      const zipFileName = createSafeFilename(
        [pageIndex && `Q${pageIndex}`, survey.title].filter(
          (e) => typeof e === "string",
        ),
      );
      await createChartsZip(chartBlobs, zipFileName);
    } else if (chartBlobs.length === 1) {
      const [chart] = chartBlobs;
      const url = URL.createObjectURL(chart.blob);
      download(url, `${chart.name}.${chart.extension}`);
    }
  } catch (error) {
    if (error.message === "Export process canceled") {
      console.warn("Export was canceled by the user.");
    } else {
      console.error("Export failed:", error);
      throw "Export failed. Please try again.";
    }
  } finally {
    setExportProgress?.({ current: 0, total: 0, isExporting: false });
  }
};
export const createChartsZip = async (
  charts: { blob: Blob; name: string; extension: string }[],
  fileName = "charts",
): Promise<void> => {
  const zip = new JSZip();

  for (const { blob, name, extension } of charts) {
    zip.file(`${name}.${extension}`, blob);
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const url = window.URL.createObjectURL(zipBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${fileName}.zip`;
  link.click();
  window.URL.revokeObjectURL(url);
};
